import React, { Dispatch, SetStateAction } from 'react';
import { Button, Dialog, Divider, Grid2, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { capitalize } from 'lodash';
import { CloseIcon } from './AppIcons';

interface Props {
  fileCount?: number;
  action: string;
  confirm: () => void;
  setShowAlert: Dispatch<SetStateAction<boolean>>;
  value?: string;
}

const useStyles = makeStyles({
  paper: {
    minWidth: '593px !important',
    minHeight: '137px !important',
    borderRadius: '21px',
    boxShadow: 'rgba(242, 242, 242, 1)',
    padding: '16px',
  },
  close: {
    fontSize: '35px !important',
    color: '#5F6774 !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
  icon: {
    '& path': {
      fill: '#2C2C2C !important',
    },
    '&.delete': {
      width: '42px !important',
      height: '42px !important',
    },
    '&.remove': {
      fontSize: '16px !important',
      marginRight: '8px',
    },
  },
  bold: {
    fontWeight: 'bolder !important',
  },
  button: {
    height: '38px',
    fontSize: '18px !important',
    fontFamily: 'Lato !important',
    backgroundColor: '#F67570 !important',
    boxShadow: 'none !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
  },
  cancel: {
    fontSize: '16px !important',
    color: '#F67570 !important',
    marginRight: '24px !important',
  },
});

const ConfirmationDialog: React.FC<Props> = ({ fileCount, action, value, confirm, setShowAlert }) => {
  const classes = useStyles();

  const getActionButtonLabel = () => {
    if (action === 'update') {
      return `Yes, Mark as ${value === 'Un Used' ? 'Not Used' : value}`;
    }
    if (action === 'RemoveProfilePic') {
      return 'Yes, Remove';
    }
    if (action === 'deleteSchedulePost') {
      return 'Remove';
    }
    return capitalize(action === 'delete' ? 'Unpublish' : action);
  };

  return (
    <Dialog
      open
      aria-modal="true"
      role="dialog"
      onClose={() => setShowAlert(false)}
      PaperProps={{
        onClick: (e: any) => e.stopPropagation(),
        className: classes.paper,
      }}
    >
      <Grid2 container justifyContent="space-between">
        <Grid2 container className={classes.bold}>
          {action === 'remove' && <span style={{ fontWeight: 'inherit' }}>Remove {fileCount} selected file?</span>}
          {(action === 'rename' || action === 'delete' || action === 'deleteSchedulePost') && (
            <span style={{ fontWeight: 'inherit' }}>Are you sure ?</span>
          )}
          {action === 'update' && <span style={{ fontWeight: 'inherit' }}>Confirm Usage Update</span>}
          {action === 'RemoveProfilePic' && <span style={{ fontWeight: 'inherit' }}>Remove Profile Picture?</span>}
        </Grid2>
        <Grid2 container>
          <IconButton onClick={() => setShowAlert(false)} role="button" sx={{ padding: 0 }}>
            <CloseIcon viewBox="0 0 42 42" className={classes.close} />
          </IconButton>
        </Grid2>
      </Grid2>

      <Grid2 mb={1}>
        <Divider
          orientation="horizontal"
          flexItem
          sx={{
            backgroundColor: '#E1E3E8',
            paddingBottom: '1px',
          }}
        />
      </Grid2>

      <Grid2 p={1}>
        {action === 'remove' && (
          <span>This file will be removed from the database. You can still recover it later if needed.</span>
        )}
        {action === 'rename' && <span>File name will be renamed.</span>}
        {action === 'deleteSchedulePost' && <span>{`${value} post will be removed from Scheduled post`}</span>}
        {action === 'delete' && <span>File will be unpublished.</span>}
        {action === 'update' && (
          <span>{`This will update the selected files  usage status in the system ${value}. Do you want to continue?`}</span>
        )}
        {action === 'RemoveProfilePic' && <span>Your profile picture will be removed.</span>}
      </Grid2>

      <Grid2 container justifyContent="end" mt={1}>
        <Button variant="text" type="button" className={classes.cancel} onClick={() => setShowAlert(false)}>
          cancel
        </Button>
        <Button
          variant="contained"
          className={classes.button}
          onClick={confirm}
          style={{ width: action === 'delete' ? '115px' : 'auto', textTransform: 'none' }}
        >
          {getActionButtonLabel()}
        </Button>
      </Grid2>
    </Dialog>
  );
};

export default ConfirmationDialog;
